import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { ArticleRenderer } from "../components/ArticleRenderer";
import { getUseCaseBySlug, USE_CASES } from "../data/useCases";
import { getToolBySlug, getToolByAffiliateKey } from "../data/tools";
import { getOutboundProps, resolveOutbound } from "../config/affiliateLinks";

export default function UseCasePage() {
  const { slug } = useParams();
  const useCase = getUseCaseBySlug(slug);
  if (!useCase) return <Navigate to="/use-cases" replace />;
  const winner = getToolBySlug(useCase.winnerSlug);
  const related = USE_CASES.filter((u) => u.slug !== slug).slice(0, 3);
  const outbound = winner ? getOutboundProps(winner.affiliateKey) : null;
  const resolved = winner ? resolveOutbound(winner.affiliateKey) : null;
  const isLive = !!(resolved && winner && resolved.key === winner.affiliateKey);
  const resolvedTool = winner && !isLive && resolved ? getToolByAffiliateKey(resolved.key) : winner;

  return (
    <>
      <SEO title={useCase.title} description={useCase.metaDescription} image={useCase.heroImage} />

      <section className="relative pt-36 pb-16 px-6 lg:px-10 spotlight">
        <div className="max-w-4xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-4">By Use Case</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
            {useCase.title}
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mt-8 rounded-sm overflow-hidden border border-white/10 aspect-[16/7]">
            <img src={useCase.heroImage} alt={useCase.title} className="w-full h-full object-cover" />
          </motion.div>
          {winner && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-6 inline-flex flex-wrap items-center gap-3 border border-gold/30 rounded-sm px-5 py-3 bg-gold/5">
              <span className="text-xs font-mono-tag uppercase tracking-wider text-gold">Winner</span>
              <span className="text-white font-display font-bold">{winner.name}</span>
              <Link to={`/reviews/${winner.slug}`} className="text-primary text-sm hover:text-[#5C7CFA] inline-flex items-center gap-1">Read review <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" /></Link>
              {outbound && (
                <a href={outbound.href} target={outbound.target} rel={outbound.rel} data-testid="usecase-winner-cta" className="text-gold hover:text-white text-sm font-medium inline-flex items-center gap-1">
                  {isLive ? `Try ${winner.name}` : `See ${resolvedTool?.name}`} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </a>
              )}
            </motion.div>
          )}
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24">
        <div className="max-w-4xl mx-auto">
          <ArticleRenderer blocks={useCase.blocks} />
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24 border-t border-white/10 pt-16">
        <div className="max-w-4xl mx-auto">
          <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-6">More Use Cases</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((u) => (
              <Link key={u.slug} to={`/use-cases/${u.slug}`} className="group border border-white/10 rounded-sm overflow-hidden hover:border-primary/50 transition-colors">
                <img src={u.heroImage} alt={u.title} className="w-full h-28 object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                <div className="p-4"><p className="text-white text-sm font-medium leading-snug">{u.title}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
